import { useCallback, useEffect, useState } from 'react';
import Box from '@mui/material/Box';
import { useQueryClient } from '@tanstack/react-query';
import Ty from '@mui/material/Typography';
import Button from '@mui/material/Button';
import CancelOutlined from '@mui/icons-material/CancelOutlined';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import CircularProgress from '@mui/material/CircularProgress';
import { usePurchaseOrderAcknowledgementStatus } from '../shared/inventory/requests/queries';
import { useUpdatePurchaseOrderAcknowledgementStatus } from '../shared/inventory/requests/mutations';
import { PurchaseOrderStatus } from '../shared/inventory/models/PurchaseOrders';
import useQueryKeys from '../shared/inventory/requests/queryKeys';
import {
  acknowledgeContentBodyStyles,
  confirmingLoaderStyles,
  containerStyles,
  contentBodyStyles,
  errorIconStyles,
  successIconStyles,
} from './styles';

function PurchaseOrderAcknowledgement() {
  const { t } = useTranslation();
  const { id } = useParams<Record<'id', string>>();
  const { orgId } = useParams<Record<'orgId', string>>();
  const { data } = usePurchaseOrderAcknowledgementStatus(id, orgId);
  const updateAcknowledgementStatus = useUpdatePurchaseOrderAcknowledgementStatus();
  const queryClient = useQueryClient();
  const queryKeys = useQueryKeys();
  const [errorState, setErrorState] = useState(false);

  const handleAcknowledge = () => {
    updateAcknowledgementStatus.mutate(
      { id, nepOrganization: orgId },
      {
        onSuccess: () => {
          queryClient.invalidateQueries({ queryKey: queryKeys.purchaseOrders.acknowledgementStatusById(id) });
        },
        onError: () => {
          setErrorState(true);
        },
      }
    );
  };

  const validateStatus = useCallback(
    (purchaseOrderStatus: PurchaseOrderStatus) => {
      const isStatusMatch = (status: PurchaseOrderStatus) => data?.status === status;
      const canShowContent = !errorState && !updateAcknowledgementStatus.isLoading;
      return data && isStatusMatch(purchaseOrderStatus) && canShowContent;
    },
    [data, errorState, updateAcknowledgementStatus.isLoading]
  );

  useEffect(() => {
    if (
      data &&
      data.status !== PurchaseOrderStatus.Sent &&
      data.status !== PurchaseOrderStatus.Unconfirmed &&
      data.status !== PurchaseOrderStatus.Confirmed
    ) {
      setErrorState(true);
    }
  }, [data]);

  return (
    <Box sx={containerStyles} data-testid='purchase-order-acknowledgement'>
      {updateAcknowledgementStatus.isLoading && (
        <>
          <Ty variant='body1' sx={confirmingLoaderStyles}>
            {t('purchaseOrderAcknowledgement.confirming')}
          </Ty>
          <CircularProgress size={32} />
        </>
      )}
      {validateStatus(PurchaseOrderStatus.Sent) && (
        <>
          <Ty variant='h4'>{t('purchaseOrderAcknowledgement.title')}</Ty>
          <Ty variant='body1' sx={acknowledgeContentBodyStyles}>
            {t('purchaseOrderAcknowledgement.text')}
          </Ty>
          <Button variant='contained' onClick={handleAcknowledge}>
            {t('purchaseOrderAcknowledgement.yes')}
          </Button>
        </>
      )}
      {validateStatus(PurchaseOrderStatus.Confirmed) && (
        <>
          <CheckCircleOutlineIcon sx={successIconStyles} />
          <Ty variant='h4'>{t('purchaseOrderAcknowledgement.purchaseOrderConfirmed')}</Ty>
          <Ty variant='body1' sx={contentBodyStyles}>
            {t('purchaseOrderAcknowledgement.confirmedDescription')}
          </Ty>
        </>
      )}
      {validateStatus(PurchaseOrderStatus.Unconfirmed) && (
        <>
          <CancelOutlined sx={errorIconStyles} />
          <Ty variant='h4'>{t('purchaseOrderAcknowledgement.purchaseOrderUnconfirmed')}</Ty>
          <Ty variant='body1' sx={contentBodyStyles}>
            {t('purchaseOrderAcknowledgement.unconfirmedDescription')}
          </Ty>
        </>
      )}
      {errorState && (
        <>
          <CancelOutlined sx={errorIconStyles} />
          <Ty variant='h4'>{t('purchaseOrderAcknowledgement.errorState')}</Ty>
          <Ty variant='body1' sx={contentBodyStyles}>
            {t('purchaseOrderAcknowledgement.errorStateDescription')}
          </Ty>
        </>
      )}
    </Box>
  );
}

export default PurchaseOrderAcknowledgement;
